import React, { useState, useEffect } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause, Heart } from 'lucide-react';

const localImages = [
    { uri: 'https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=1000&auto=format&fit=crop', caption: 'Beautiful Memory #1' },
    { uri: 'https://images.unsplash.com/photo-1511688855354-12fe43a2861a?q=80&w=1000&auto=format&fit=crop', caption: 'Beautiful Memory #2' },
    { uri: 'https://images.unsplash.com/photo-1494774157365-9e04c6720e47?q=80&w=1000&auto=format&fit=crop', caption: 'Beautiful Memory #3' },
    { uri: 'https://images.unsplash.com/photo-1549416805-04537159c382?q=80&w=1000&auto=format&fit=crop', caption: 'Beautiful Memory #4' },
    { uri: 'https://images.unsplash.com/photo-1516589174184-c685eb3da121?q=80&w=1000&auto=format&fit=crop', caption: 'Beautiful Memory #5' },
];

const ImageSlider: React.FC = () => {
    const [index, setIndex] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(false);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isAutoPlay) {
            interval = setInterval(() => {
                setIndex((prev) => (prev + 1) % localImages.length);
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [isAutoPlay]);

    const next = () => setIndex((prev) => (prev + 1) % localImages.length);
    const prev = () => setIndex((prev) => (prev - 1 + localImages.length) % localImages.length);

    return (
        <div className="px-6 py-4 flex flex-col items-center">
            <div className="w-full max-w-6xl relative group">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="aspect-[4/5] md:aspect-[3/1] rounded-[3rem] overflow-hidden shadow-2xl relative border-4 border-white dark:border-gray-800"
                    >
                        <img
                            src={localImages[index].uri}
                            alt={localImages[index].caption}
                            className="w-full h-full object-cover"
                        />

                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex flex-col justify-end p-6 md:p-10 text-left">
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                className="text-white"
                            >
                                <h3 className="text-lg md:text-xl font-playfair font-bold mb-1">{localImages[index].caption}</h3>
                                <p className="text-[10px] md:text-xs opacity-80 font-poppins text-gray-200">A memory to cherish forever</p>
                            </motion.div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Navigation */}
                <button
                    onClick={prev}
                    className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-md flex items-center justify-center text-primary-red shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <ChevronLeft size={20} />
                </button>
                <button
                    onClick={next}
                    className="absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-md flex items-center justify-center text-primary-red shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <ChevronRight size={20} />
                </button>
            </div>

            {/* Controls Area */}
            <div className="mt-4 w-full max-w-6xl flex flex-col items-center">
                <div className="flex items-center justify-between w-full mb-4 px-4">
                    <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                        Moment {index + 1} of {localImages.length}
                    </span>

                    <button
                        onClick={() => setIsAutoPlay(!isAutoPlay)}
                        className={`flex items-center space-x-2 px-5 py-2 rounded-full font-bold transition-all shadow-md text-xs ${isAutoPlay ? 'bg-primary-red text-white' : 'bg-primary-red/10 text-primary-red'
                            }`}
                    >
                        {isAutoPlay ? <Pause size={14} /> : <Play size={14} />}
                        <span>{isAutoPlay ? 'Pause' : 'Play'}</span>
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-100 dark:bg-gray-800 h-1.5 rounded-full overflow-hidden mb-6">
                    <motion.div
                        className="h-full bg-primary-red"
                        animate={{ width: `${((index + 1) / localImages.length) * 100}%` }}
                    />
                </div>

                {/* Mini Heart Indicators */}
                <div className="flex space-x-3 overflow-x-auto p-4 max-w-full scrollbar-none">
                    {localImages.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => { setIndex(i); setIsAutoPlay(false); }}
                            className={`transition-all duration-300 transform ${i === index ? 'scale-125 text-primary-red' : 'scale-100 text-gray-300 hover:text-primary-red/50'}`}
                        >
                            <Heart size={20} fill={i <= index ? 'currentColor' : 'none'} />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ImageSlider;
